<template>
    <div class="container" :class="{ 'no-menu' : hasmenu() }">
        <div v-if="!logo">
            <div class="logo-container">
                <div class="img-container">
                    <img
                        class="logo"
                        src="../assets/img/highq_logo.png"
                        @click="toggleTheme"
                    />
                </div>
            </div>

            <div class="title-container" @click="goHome">
                <div class="title">
                    {{ title }}
                </div>
            </div>
        </div>
        <div
            v-else
            class="client-logo-container"
            @click="goHome"
        >
            <img id="logo" :src="logo" />
        </div>

        <div
            v-if="showMenu"
            class="menu-toggle-container"
        >
            <fa-icon
                class="menu-icon"
                icon="fa-solid fa-bars"
                @click.stop="toggleMenu"
            />
        </div>

        <div v-if="notLogin" class="actions narrow">
            <div>
                <fa-icon
                    icon="fa-solid fa-sign-out-alt"
                    class="logout"
                    @click="logout"
                />
            </div>
            <div>
                <fa-icon
                    icon="fa-solid fa-key"
                    class="change-password"
                    @click="changePassword"
                />
            </div>
        </div>

        <div
            v-if="showMenu"
            class="menu-container-wrapper"
            :class="{ 'menu-toggled' : menuToggled }"
        >
            <div
                id="navbar"
                class="menu-container"
                :class="{ 'menu-toggled' : menuToggled }"
            >
                <div
                    v-for="entry in getNavigationList()"
                    :key="entry.id"
                    class="item-container"
                    @click="hideMenu()"
                >
                    <router-link
                        v-slot="{ navigate, isActive, isExactActive }"
                        custom
                        :to="entry.path"
                    >
                        <div
                            :class="{
                                'router-link-active': isActive,
                                'router-link-exact-active': isExactActive,
                            }"
                            :href="entry.path"
                            @click="navigate"
                        >
                            {{ $t(entry.meta.display) }}
                        </div>
                    </router-link>
                </div>
            </div>

            <div v-if="notLogin" class="actions">
                <div>
                    <fa-icon
                        icon="fa-solid fa-sign-out-alt"
                        class="logout"
                        @click="logout"
                    />
                </div>
                <div>
                    <fa-icon
                        icon="fa-solid fa-key"
                        class="change-password"
                        @click="changePassword"
                    />
                </div>
            </div>
        </div>

        <Modal
            v-if="showChangePassword"
            :isopen="showChangePassword"
            :isloading="isLoadingChangePassword"
            :title="$t('navbar.changepassword')"
            :submitlabel="$t('navbar.buttons.confirm')"
            :cancellabel="$t('navbar.buttons.abort')"
            @close="hideModal"
        >
            <template #content>
                <FloatingInput
                    v-model="resetPasswordInput.oldPassword"
                    :title="$t('navbar.oldpassword')"
                    :isloading="isLoadingChangePassword"
                    :error="resetPasswordValidation.oldpassword.$error"
                    :isrequired="true"
                    :password="true"
                />
                <div class="spacer"></div>
                <FloatingInput
                    v-model="resetPasswordInput.newPassword"
                    :title="$t('navbar.newpassword')"
                    :isloading="isLoadingChangePassword"
                    :error="resetPasswordValidation.newpassword.$error"
                    :isrequired="true"
                    :password="true"
                />
                <div class="spacer"></div>
                <FloatingInput
                    v-model="resetPasswordInput.newPasswordConfirm"
                    :title="$t('navbar.newpasswordconfirm')"
                    :isloading="isLoadingChangePassword"
                    :error="resetPasswordValidation.newpasswordconfirm.$error"
                    :isrequired="true"
                    :password="true"
                />
                <div class="spacer"></div>
            </template>
        </Modal>
    </div>
</template>

<script setup>
import FloatingInput from '@elements/FloatingInput.vue'
import Modal from '@elements/Modal.vue'
import { required, sameAs } from '@vuelidate/validators'

import getEnv from '@/utils/env.js'

import { useStorageManipulation } from '@/plugins/storage-manipulation.js'
import { useToast } from '@/plugins/toast/index.js'
import useVuelidate from '@vuelidate/core'
import axios from 'axios'
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

defineProps({
    title: {
        type: String,
        default: 'Vuepoc'
    }
})

const { t }  = useI18n()
const route  = useRoute()
const router = useRouter()
const store  = useStore()
const toast  = useToast()
const storageManipulation = useStorageManipulation()

const menuToggled             = ref(false)
const showChangePassword      = ref(false)
const isLoadingChangePassword = ref(false)

const resetPasswordInput = reactive({
    oldPassword: undefined,
    newPassword: undefined,
    newPasswordConfirm: undefined
})
const resetPasswordRules = {
    oldPassword: { required },
    newPassword: { required },
    newPasswordConfirm: {
        required,
        sameAsPassword: sameAs('newPassword')
    }
}
const resetPasswordValidation = useVuelidate(resetPasswordRules, resetPasswordInput)


const showMenu = computed(() => route.meta?.menu)
const notLogin = computed(() => !route.path.includes('login'))
const logo     = computed(() => notLogin.value && store.state.clientlogo)

const hideModal = async (submit) => {
    if (!submit) {
        showChangePassword.value = false
        return
    }

    resetPasswordValidation.value.$touch()

    if (resetPasswordValidation.value.$error) {
        return
    }

    isLoadingChangePassword.value = true

    let url = getEnv('VITE_MIDDLEWARE_URL')
    url += '/changepassword/' + storageManipulation.getSessionData().client

    const body = {
        username: storageManipulation.getSessionData().username,
        oldpassword: resetPasswordInput.oldPassword,
        newpassword: resetPasswordInput.newPassword,
        newpasswordconfirm: resetPasswordInput.newPasswordConfirm
    }

    return axios.post(url, body)
    .then(response => {
        var status = response.data.status

        if (status.ErrorCode !== 0) {
            toast.error(status.ErrorMsg)
        } else {
            toast.info(t('navbar.changepasswordsuccess'))
            showChangePassword.value = false
            storageManipulation.clearSessionData()
            router.push({ name: 'login' })
        }

        isLoadingChangePassword.value = false
    })
    .catch(err => {
        toast.error(err)
        isLoadingChangePassword.value = false
    })
}

const logout = () => {
    storageManipulation.clearSessionData()

    let login = '/login'

    if (storageManipulation.getDefaultClient()) {
        login += '?client=' + storageManipulation.getDefaultClient()
    }

    router.push(login)
}

const changePassword = () => showChangePassword.value = true
const goHome         = () => router.push({ name: 'root' })
const toggleMenu     = () => {
    menuToggled.value = !menuToggled.value

    if (menuToggled.value) {
        document.addEventListener('click', () => hideMenu(), { once: true })
    }
}
const hideMenu       = () => menuToggled.value = false
const hasmenu        = () => !route.meta.menu

const getNavigationList = () => router.getRoutes().filter(route => route.meta.menuentry)

const toggleTheme = () => {
    const  active = document.querySelector('#alt-theme')
    if (!active) {
        addLightTheme()
    } else {
        removeLightTheme()
    }
}

const addLightTheme = () => {
    const themeEl = document.createElement('link')
    themeEl.setAttribute('rel', 'stylesheet')
    themeEl.setAttribute('href', import.meta.env.BASE_URL + '/css/alt-theme.css')
    themeEl.setAttribute('id', 'alt-theme')

    const docHead = document.querySelector('html')
    docHead.append(themeEl)
    localStorage['hqtheme'] = JSON.stringify(true)
}

const removeLightTheme = () => {
    const themeEl = document.querySelector('#alt-theme')
    const parentNode = themeEl.parentNode
    parentNode.removeChild(themeEl)
    localStorage['hqtheme'] = JSON.stringify(false)
}

onMounted(() => {
    if (localStorage['hqtheme'] && JSON.parse(localStorage['hqtheme']) && !document.querySelector('#alt-theme')) {
        addLightTheme()
    }
})
</script>

<style lang="scss" scoped>
.spacer {
    margin-top: 20px;
}

.container {
    text-align: left;
    height: fit-content;
    background: var(--color-fg-default);
    border-color: var(--color-border-bright-default);
    z-index: 100;

    .logo-container {
        margin-left: 50px;
        height: 60px;
        width: fit-content;
        float: left;
        display: table;

        > .img-container {
            display: table-cell;
            vertical-align: middle;

            > .logo {
                height: 45px;
            }
        }
    }

    .client-logo-container {
        cursor: pointer;

        > #logo {
            display: block;
            box-sizing: border-box;
            padding-top: 5px;
            max-height: 60px;
            max-width: 300px;
            margin: auto;
        }
    }

    .title-container {
        float: left;
        margin-left: 25px;
        font-size: 1.2em;
        width: 170px;
        height: 60px;
        display: table;

        cursor: pointer;

        > .title {
            display: table-cell;
            vertical-align: middle;
        }

        > #spinner {
            position: absolute;
            right: 5px;
            transform: scale(0.6);
            top: -5px;
        }
    }

    > .menu-container-wrapper {
        > .menu-container {
            margin: 10px;
            margin-top: 0px;
            margin-left: 0px;
            background-color: var(--color-fg-default);
            padding-top: 20px;
            box-shadow: var(--shadow-default);

            > .item-container {
                width: 90%;
                height: 40px;
                margin: 5px 15px;
                line-height: 40px;
                font-size: 1em;

                cursor: pointer;
                * { cursor: pointer; }

                &:hover {
                    > a {
                        &:not(.router-link-active) {
                            cursor: pointer;
                            background-color: var(--color-hover-highlight-10);
                            border-radius: var(--setting-border-radius-big-default)
                        }
                    }
                }
            }
        }
    }

    > .menu-toggle-container {
        > .menu-icon {
            cursor: pointer;
        }
    }

    .actions {
        position: absolute;
        bottom: 16px;
        left: 5px;
        z-index: 5;
        display: flex;

        &.narrow {
            left: unset;
            right: 50px;
            top: 14px;
            visibility: hidden;
        }

        > div {
            > svg {
                width: 30px;
                height: 30px;
                padding: 5px;
                box-sizing: border-box;
                border-radius: var(--setting-border-radius-big-default);
                cursor: pointer;

                &:hover {
                    background: var(--color-hover-highlight-10);
                }
            }
        }

        .logout {
            color: red;
        }

        .change-password {
            margin-left: 10px;
        }
    }
}

@media(min-width: 1000px) {
    .container {
        width: 300px;
        height: 60px;
        box-shadow: var(--shadow-default);

        &.no-menu {
            border-radius: 0 0 var(--setting-border-radius-big-default) 0;
        }
    }

    /* nav height + 100px */
    /* menu is only sticky if the screen is large enough to display it */
    @media(min-height: 600px) {
        .container {
            position: fixed;
        }
    }

    @media(max-height: 600px) {
        .container {
            position: absolute;
        }
    }

    .menu-toggle-container {
        visibility: hidden;
    }

    .menu-container-wrapper {
        overflow: hidden;
        transition: transform .2s;
        position: absolute;
        top: 60px;
        left: 0;
        height: fit-content;
    }

    .menu-container {
        height: 100%;
        width: 300px;
        padding-bottom: 60px;
        border-radius: 0 0 var(--setting-border-radius-big-default) 0;
    }

    .item-container > div {
        padding-left: 30px;
        text-align: left;
        box-sizing: border-box;
    }

    .router-link-active {
        position: relative;
        box-shadow: var(--shadow-highlight);
        border-radius: var(--setting-border-radius-big-default);
        background-color: var(--color-highlight-default);
        color: var(--color-font-highlight-default)
    }
}

@media(max-width: 1000px) {
    #spinner {
        left: 230px;
        right: unset;
    }

    #logo {
        position: absolute;
        left: 10px;
    }

    .container {
        width: 100%;
        height: 60px;
        position: fixed;
        box-shadow: var(--shadow-default);
    }

    .menu-toggle-container {
        position: absolute;
        /* make space for the icon which toggles the theme */
        width: calc(100% - 100px);
        text-align: right;
        height: 60px;
        line-height: 60px;
        padding-right: 20px;
        box-sizing: border-box;
        /* make space for the icon which toggles the theme */
        left: 100px;
    }

    .menu-container {
        width: 100%;
        height: fit-content;
        border-radius: 0 0 var(--setting-border-radius-big-default) var(--setting-border-radius-big-default);
        overflow: hidden;
        visibility: hidden;
    }

    .menu-container-wrapper {
        visibility: hidden;
    }

    .item-container {
        text-align: center;
    }

    .menu-container.menu-toggled {
        margin-top: 60px;
        visibility: visible;
        box-shadow: 0px 5px 5px rgb(0 0 0 / 10%);
    }

    .router-link-active {
        margin: 0 auto;
        box-shadow: var(--shadow-highlight);
        border-radius: var(--setting-border-radius-big-default);
        background-color: var(--color-highlight-default);
        color: var(--color-font-highlight-default)
    }

    .container .actions.narrow {
        position: absolute;
        top: 8px;
        right: 70px;
        left: unset;
        visibility: visible;
    }
}
</style>